import { cn } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Check, CircleSlash, Settings2Icon } from 'lucide-react'
import EventActivityHeaderContextMenu from './EventActivityHeaderContextMenu'
import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import EventActivitySummaryOptionContent from './EventActivitySummaryOptionContent'

type EventActivityHeaderCellProps = {
  activity: wowaudit.EventActivity
  activities: wowaudit.EventActivity[]
  stickyTop: string
  canAdminEdit: boolean
  allSelectedForActivity: boolean
  issues: { selectedCount: number; tooManyPlayers: boolean }
  isPlannedActivity: boolean
  isPlanningMode: boolean
  hasLinkedPlanIssues: boolean
  linkedPlanIssueCount: number
  onPlanClick: (activityId: number) => void
}

const EventActivityHeaderCell = ({
  activity,
  activities,
  stickyTop,
  canAdminEdit,
  allSelectedForActivity,
  issues,
  isPlannedActivity,
  isPlanningMode,
  hasLinkedPlanIssues,
  linkedPlanIssueCount,
  onPlanClick,
}: EventActivityHeaderCellProps) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `event-activity-${activity.id}`,
    disabled: !canAdminEdit,
  })

  const [isSmall, setIsSmall] = useState(false)
  const contentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = contentRef.current
    if (!node) return

    const observer = new ResizeObserver(() => {
      const nextIsSmall = node.offsetWidth < 97
      setIsSmall((previousIsSmall) =>
        previousIsSmall === nextIsSmall ? previousIsSmall : nextIsSmall,
      )
    })

    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [])

  const iconInteractionProps = canAdminEdit
    ? ({ ...attributes, ...listeners } as HTMLAttributes<HTMLDivElement>)
    : undefined
  const activityCellContent = (
    <div ref={contentRef} className={cn('px-2 max-w-48', isSmall && 'px-0')}>
      <EventActivitySummaryOptionContent
        activity={activity}
        issues={issues}
        hasLinkedPlanIssues={hasLinkedPlanIssues}
        linkedPlanIssueCount={linkedPlanIssueCount}
        compact={isSmall}
        onIconRef={setActivatorNodeRef}
        iconInteractionProps={iconInteractionProps}
        isIconDraggable={canAdminEdit}
        subtitle={
          canAdminEdit ? (
            <EventActivityHeaderContextMenu
              activity={activity}
              activities={activities}
              allSelected={allSelectedForActivity}
              trigger={
                <Button
                  type='button'
                  variant='outline'
                  size='xs'
                  className='w-full'
                >
                  <Settings2Icon className='h-3.5 w-3.5' />
                </Button>
              }
            />
          ) : (
            activity.instance?.name
          )
        }
      />
      <Button
        type='button'
        variant={isPlannedActivity ? 'default' : 'outline'}
        size='xs'
        className={cn(
          'w-full mt-1 gap-1.5 px-0',
          isPlannedActivity
            ? ''
            : !activity.raidPlanId
            ? 'text-muted-foreground'
            : 'text-success-foreground border-success-foreground',
        )}
        onClick={() => onPlanClick(activity.id)}
      >
        {activity.encounter ? (
          <>
            {activity.raidPlanId ? (
              <Check className='h-3 w-3 min-w-3 min-h-3' />
            ) : (
              <CircleSlash className='h-3 w-3' />
            )}
            Plan
          </>
        ) : (
          'Setup'
        )}
      </Button>
    </div>
  )

  return (
    <th
      ref={setNodeRef}
      className={cn(
        'sticky z-30 bg-card border-b border-x border-x-transparent font-normal text-xs px-0 py-1 overflow-visible',
        stickyTop,
        isPlanningMode && !isPlannedActivity && 'opacity-65',
        isPlannedActivity && 'bg-accent border-x-border border-b-primary',
      )}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 40 : undefined,
      }}
    >
      {activityCellContent}
    </th>
  )
}

export default EventActivityHeaderCell
